import { isSedeMode } from '../../../utils/partnerAccess/partnerStorage';
import { isHumanTasksFixturesMode } from './helpers/fixtures-mode';

export const INICIO_ROUTE = '/inicio';
const DEFAULT_LANDING_ROUTE = '/chat';

const NO_ACCESS = Object.freeze({ enabled: false, receivesTasks: false, canSeeStats: false });
const FIXTURES_ACCESS = Object.freeze({ enabled: true, receivesTasks: true, canSeeStats: true });

/**
 * Single access rule of Inicio (tasks for human agents), shared by the nav,
 * the `/inicio` route guard, the landing after login and the Pusher
 * subscription. It reads the session field `userData.humanTasks` =
 * `{ enabled, receivesTasks, canSeeStats }` that `/verifyToken` carries;
 * a missing field (today's backend) means no access at all.
 *
 * - `enabled`: the company has the feature (flag `taskQueue`).
 * - `receivesTasks`: this user gets tasks ("recibe tareas").
 * - `canSeeStats`: permission `taskStats` (the "Estadísticas de agentes" tab).
 *
 * Only the boolean `true` counts. A sede tab (partner access) never gets
 * Inicio, so it keeps its current behavior. In example-data mode any logged-in
 * user gets everything.
 */
export function getHumanTasksAccess(userData) {
  if (!userData || typeof userData !== 'object') return NO_ACCESS;
  if (isSedeMode()) return NO_ACCESS;
  if (isHumanTasksFixturesMode()) return FIXTURES_ACCESS;

  const flags = userData.humanTasks;
  if (!flags || flags.enabled !== true) return NO_ACCESS;
  return {
    enabled: true,
    receivesTasks: flags.receivesTasks === true,
    canSeeStats: flags.canSeeStats === true,
  };
}

/** The user gets tasks: "Mis tareas", the nav badge, the toast and the landing. */
export const receivesHumanTasks = (userData) => getHumanTasksAccess(userData).receivesTasks;

/** The "Estadísticas de agentes" tab. */
export const canSeeTaskStats = (userData) => getHumanTasksAccess(userData).canSeeStats;

/** The "Inicio" nav entry and the `/inicio` route. */
export const canSeeInicio = (userData) => {
  const access = getHumanTasksAccess(userData);
  return access.receivesTasks || access.canSeeStats;
};

/**
 * Where a user lands after login: Inicio for those who receive tasks, the chat
 * for everybody else (unchanged). The company status screens (payment,
 * WhatsApp linking) are decided by the callers before this.
 */
export const getLandingRoute = (userData) =>
  (canSeeInicio(userData) && receivesHumanTasks(userData) ? INICIO_ROUTE : DEFAULT_LANDING_ROUTE);
